import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ViewIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  cloneWorkout,
  deleteWod,
  fetchPastWods,
} from "../../services/WodService";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { WOD } from "./types";
import { getReadableDate, getReadableShortTime } from "../../utils/commons";
import { useWod } from "../../context/WODContext";
import Lottie from "react-lottie";
import RocketImage from "../../assets/empty.json";
import LoadingImage from "../../assets/loading.json";
import { WODView } from "./WODView";
import { SystemDialog } from "../../utils/Dialog";
import { useSnackbar } from "notistack";

const emptyOptions = {
  loop: true,
  autoplay: true,
  animationData: RocketImage,
};

const loadingOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingImage,
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface IViewWods {
  onEditWodSelected: () => void;
}

const ViewWODs = ({ onEditWodSelected }: IViewWods) => {
  const [wods, setWods] = useState<Array<WOD>>([]);
  const { setWodSets, setIsEditing: setWodIsEditing, setWodDate } = useWod();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [currentWod, setCurrentWod] = useState<WOD | undefined>(undefined);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData>>();
  const [hasMore, setHasMore] = useState<boolean>(false);

  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    useState<boolean>(false);

  const [showWODClone, setShowWODClone] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const getPastWorkouts = () => {
    setLoading(true);
    fetchPastWods()
      .then((result) => {
        setWods(result.wods);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        alert("Something went wrong! Please try again later...");
      });
  };

  const loadMoreWorkouts = () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    fetchPastWods(lastDoc)
      .then((result) => {
        setWods((prev) => [...prev, ...result.wods]);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
        setLoadingMore(false);
      })
      .catch((e) => {
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    getPastWorkouts();
  }, []);

  const onEditClicked = (wod: WOD) => {
    setWodSets(wod.sets);
    setWodIsEditing(true);
    setWodDate(wod.date);
    onEditWodSelected();
  };

  const onViewClicked = (wod: WOD) => {
    setCurrentWod(wod);
  };

  const onDeletWodClicked = (wod: WOD) => {
    setCurrentWod(wod);
    onWodDeleteClicked();
  };

  const onWodDeleteClicked = () => {
    setShowDeleteConfirmDialog(true);
  };

  const handleCloneClicked = () => {
    setShowWODClone(true);
  };

  const handleDeleteConfirm = () => {
    deleteWod(currentWod?.date)
      .then(() => {
        setShowDeleteConfirmDialog(false);
        enqueueSnackbar("WOD deleted successfully!");
        setCurrentWod(undefined);
        getPastWorkouts();
      })
      .catch(() => {
        setShowDeleteConfirmDialog(false);
      });
  };

  const handleWODCloneConfirm = () => {
    if (currentWod) {
      cloneWorkout(currentWod)
        .then((cloned) => {
          enqueueSnackbar("WOD saved successfully!");
          getPastWorkouts();
          setShowWODClone(false);
        })
        .catch((e) => {
          alert("Something went wrong. Please try again later");
        });
    }
  };

  const EmptyData = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <Lottie options={emptyOptions} height={120} width={120} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}
        >
          No workouts found. Create one to get started!
        </Typography>
      </Box>
    );
  };

  const Loading = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Lottie options={loadingOptions} height={200} width={200} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          Loading your past workouts...
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "row",
        gap: 2,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Left: Workout Table */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {wods.length === 0 || loading ? (
          loading ? (
            <Loading />
          ) : (
            <EmptyData />
          )
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: 8,
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "action.hover",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "text.disabled",
                borderRadius: 4,
                "&:hover": {
                  bgcolor: "text.secondary",
                },
              },
            }}
          >
            <Table stickyHeader aria-label="past workouts table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell align="center">Duration</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wods.map((wod) => (
                  <StyledTableRow
                    key={wod.date}
                    onClick={() => onViewClicked(wod)}
                    sx={{
                      cursor: "pointer",
                      ...(currentWod?.date === wod.date && {
                        backgroundColor: "action.selected",
                      }),
                    }}
                  >
                    <StyledTableCell component="th" scope="row">
                      <Typography variant="body2">
                        {getReadableDate(wod.date).toString()}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Chip
                        label={getReadableShortTime(Number(wod?.duration))}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Tooltip title="View" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewClicked(wod);
                          }}
                          size="small"
                          sx={{ color: "text.secondary" }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditClicked(wod);
                          }}
                          size="small"
                          sx={{ color: "text.secondary", mx: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletWodClicked(wod);
                          }}
                          size="small"
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "error.main",
                              bgcolor: "rgba(244,67,54,0.08)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {hasMore && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1.5,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={loadMoreWorkouts}
              disabled={loadingMore}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Right: WOD Detail */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {currentWod ? (
          <WODView
            onDeleteWod={onWodDeleteClicked}
            onCloneClicked={handleCloneClicked}
            wod={currentWod}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select a workout to view details
            </Typography>
          </Box>
        )}
      </Box>
      <SystemDialog
        component={
          <Typography variant="overline">
            {`Are you sure you want to delete this workout ?`}
          </Typography>
        }
        open={showDeleteConfirmDialog}
        handleClose={() => setShowDeleteConfirmDialog(false)}
        handleConfirm={handleDeleteConfirm}
        heading="Delete Workout"
      />
      <SystemDialog
        component={
          <Typography variant="overline">
            {`Do you want to use this workout plan for today ?`}
          </Typography>
        }
        open={showWODClone}
        handleClose={() => setShowWODClone(false)}
        handleConfirm={handleWODCloneConfirm}
        heading="Copy Workout"
      />
    </Box>
  );
};

export default ViewWODs;
